import React, { useState } from 'react';

import classNames from 'classnames';
import PersonDetailsIcon from 'icons/person-details.svg';
import SearchOffIcon from 'icons/search-off.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { useInfiniteProjectsTeamSearchesQuery } from 'components/hooks/projectsTeamSearchQueries';
import useDebounce from 'components/hooks/useDebounce';

import Checkbox from 'components/application/Checkbox';
import SearchField from 'components/application/SearchField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

function buildSearchParams({ search, selectedTeamIds }) {
  return { search, selected_ids: [...selectedTeamIds] };
}

export default function TeamsTab({
  isComputingSelections,
  onCheckboxChange,
  selectedTeamIds,
}) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { projectId } = useProjectPersonnelContext();

  const [currentSearch, setCurrentSearch] = useState('');

  // Search params are snapshotted rather than derived from the current selections
  // so that checking/unchecking a team never changes the query key
  const [searchParams, setSearchParams] = useState(() =>
    buildSearchParams({ search: '', selectedTeamIds }),
  );

  useDebounce(currentSearch, 250, {
    onChange: ({ currentValue }) =>
      setSearchParams(
        buildSearchParams({ search: currentValue, selectedTeamIds }),
      ),
  });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectPersonnel.components.assignPersonnelSidePanel.components.TeamsTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const {
    data: domainTeams,
    fetchNextPage: fetchNextDomainTeamsPage,
    hasNextPage: hasNextDomainTeamsPage,
    isFetching: isDomainTeamsFetching,
    isSuccess: isDomainTeamsSuccess,
  } = useInfiniteProjectsTeamSearchesQuery({
    params: searchParams,
    projectId,
    queryOptions: { keepPreviousData: true },
  });

  const handleInViewChange = (isInView) => {
    if (isInView) fetchNextDomainTeamsPage();
  };

  const { scopedCount, totalCount } = domainTeams?.meta || {};

  return (
    <>
      <div className='tw-p-5'>
        <SearchField
          additionalClasses='tw-w-full'
          isDisabled={totalCount === 0}
          name='search'
          onChange={(event) => setCurrentSearch(event.target.value)}
          onReset={() => setCurrentSearch('')}
          placeholder={i18nT('search.placeholder')}
          value={currentSearch}
        />
      </div>
      {/* Fixed height needed for vertical scrolling for this section within side panel */}
      <div className='tw-flex tw-h-[calc(100vh-270px)] tw-flex-col tw-gap-3'>
        {isDomainTeamsSuccess ?
          <div className='tw-flex tw-flex-col tw-gap-1 tw-px-5 tw-font-inter'>
            <p className='tw-m-0 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-900'>
              {totalCount === 0 ?
                i18nT('results.title.noTeams')
              : searchParams.search ?
                i18nT('results.title.search', {
                  count: scopedCount,
                  interpolation: { escapeValue: false },
                  search: searchParams.search,
                })
              : i18nT('results.title.default', { count: totalCount })}
            </p>
            <p className='tw-m-0 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-grey-400'>
              {i18nT('results.description')}
            </p>
          </div>
        : <div className='tw-mx-5 tw-h-3 tw-w-50 tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
        }
        {!isDomainTeamsSuccess || scopedCount > 0 ?
          <div className='tw-h-full tw-overflow-y-auto tw-px-5'>
            {isDomainTeamsSuccess ?
              <>
                {domainTeams.data.map((team, index) => {
                  const {
                    attributes: { name },
                    id: teamId,
                    meta: { alreadyAddedPersonnelCount, personnelCount },
                  } = team;

                  const addablePersonnelCount =
                    personnelCount - alreadyAddedPersonnelCount;

                  const isSelected = selectedTeamIds.has(teamId);

                  return (
                    <div
                      className={classNames(
                        'tw-flex tw-h-18 tw-items-center tw-gap-3 tw-py-4',
                        {
                          'tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100':
                            index !== 0,
                        },
                      )}
                      key={teamId}
                    >
                      <div className='tw-px-2'>
                        <Checkbox
                          isChecked={
                            isSelected ||
                            (personnelCount > 0 && addablePersonnelCount === 0)
                          }
                          isDisabled={
                            addablePersonnelCount === 0 || isComputingSelections
                          }
                          label={i18nTCommon(
                            isSelected ?
                              'shared.deselectName'
                            : 'shared.selectName',
                            { interpolation: { escapeValue: false }, name },
                          )}
                          name='select-team'
                          onChange={() => onCheckboxChange({ teamId })}
                        />
                      </div>
                      <div className='tw-flex tw-flex-col tw-gap-1 tw-truncate tw-font-inter tw-tracking-auto'>
                        <p className='tw-m-0 tw-truncate tw-text-m tw-font-semibold tw-text-grey-700'>
                          {name}
                        </p>
                        <p className='tw-m-0 tw-truncate tw-text-s tw-font-normal tw-text-grey-500'>
                          {alreadyAddedPersonnelCount === 0 ?
                            i18nTCommon('personnel.personnelWithCount', {
                              count: personnelCount,
                            })
                          : i18nT('results.personnelCount', {
                              alreadyAddedCount: alreadyAddedPersonnelCount,
                              count: personnelCount,
                              interpolation: { escapeValue: false },
                              singularRAMSName,
                            })
                          }
                        </p>
                      </div>
                    </div>
                  );
                })}
                {hasNextDomainTeamsPage && !isDomainTeamsFetching && (
                  <InView
                    as='div'
                    className='tw-h-1 tw-w-full'
                    onChange={handleInViewChange}
                  />
                )}
              </>
            : <Skeleton />}
          </div>
        : (() => {
            const Icon = totalCount === 0 ? PersonDetailsIcon : SearchOffIcon;

            return (
              <div className='tw-mt-8 tw-flex tw-flex-col tw-items-center tw-gap-1 tw-px-5'>
                <Icon className='tw-h-12 tw-w-12 [&_path]:tw-fill-grey-300' />
                <p className='tw-m-0 tw-font-inter tw-text-l tw-font-semibold tw-text-grey-900'>
                  {i18nTCommon(
                    totalCount === 0 ? 'teams.noTeamsAdded' : (
                      'teams.noTeamsFound'
                    ),
                  )}
                </p>
              </div>
            );
          })()
        }
      </div>
    </>
  );
}

TeamsTab.propTypes = {
  isComputingSelections: PropTypes.bool.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  selectedTeamIds: PropTypes.instanceOf(Set).isRequired,
};

function Skeleton() {
  return Array.from({ length: 8 }, (_, index) => (
    <div
      className={classNames(
        'tw-flex tw-h-18 tw-items-center tw-gap-3 tw-py-4',
        {
          'tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100':
            index !== 0,
        },
      )}
      key={index}
    >
      <div className='tw-mx-2 tw-h-4 tw-w-4 tw-animate-pulse tw-rounded-sm tw-bg-grey-100' />
      <div className='tw-flex tw-flex-col tw-gap-1'>
        <div className='tw-h-3 tw-w-40 tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
        <div className='tw-h-3 tw-w-18 tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
      </div>
    </div>
  ));
}
